import { FormControlLabel, FormHelperText, Radio, RadioGroup, Stack } from '@mui/material';
import { addTimeZone } from '@onevision-resources/date-utils';
import { V2_FIELD_HEIGHT } from '@onevision-resources/design-theme';
import { getFullName } from '@provision/formatting-utils';
import { QueryBuilderMaterial } from '@react-querybuilder/material';
import dayjs from 'dayjs';
import { Trash2 } from 'lucide-react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { FullField, RuleGroupType } from 'react-querybuilder';

import { Frequency } from 'rrule';
import { ConditionsBuilder } from 'src/components/ConditionsBuilder';
import { FormAutocomplete } from 'src/components/Forms/FormAutocomplete/form-autocomplete';
import { FormDatePicker } from 'src/components/Forms/FormDatePicker/form-date-picker';
import { FormSelect } from 'src/components/Forms/FormSelect/form-select';
import { FormSwitch } from 'src/components/Forms/FormSwitch/form-switch';
import { FormToggleGroup } from 'src/components/Forms/FormToggleGroup/form-toggle-group';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import {
  BehaviorTrigger,
  mapConditionToFormValues,
  mapUIRulesForRequest,
  RulesArray,
  SelectorValue,
} from 'src/graphql';
import type { ServerPlatformUserRaw } from 'src/graphql/entities/platform-user/platform-user';
import {
  useCreateSignalOnCallScheduleEvent,
  useDeleteSignalOnCallScheduleEvent,
  useUpdateSignalOnCallScheduleEvent,
} from 'src/graphql/entities/signal-oncall-schedule/hooks/signal-oncall-schedule';
import {
  DeleteRecurrence,
  EditRecurrence,
  type OncallManagementScheduleDataInput,
  type OncallManagementScheduleMutationRecord,
  type OncallManagementScheduleRecord,
  RecurrenceOptions,
} from 'src/graphql/entities/signal-oncall-schedule/signal-oncall-schedule.types';
import { getConditionDefaultValue } from 'src/pages/Settings/rules/condition-default-value';
import { muiComponents } from 'src/pages/Settings/rules/rules-form-config';
import { ConditionValueEditor } from 'src/pages/Settings/rules/rules-form-ui-controls';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useConfirmationModal } from 'src/utils/hooks';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import {
  getValidConditionsCount,
  pruneIncompleteConditions,
} from 'src/utils/query-builder-conditions';

import {
  RECURRENCE_INTERVAL_OPTIONS,
  RECURRENCE_OPTIONS,
  RECURRENCE_PERIOD_OPTIONS,
  TIME_SLOT_OPTIONS,
  UNTIL_SLOTS,
  WEEKDAYS,
} from './helpers/const';
import {
  canEditCurrentEvent,
  getRecurrenceDayLabels,
  getRecurrenceInterval,
  getRecurrencePeriod,
  isAllDaySchedule,
  isWeeklyPeriodSelected,
  mergeDateAndTime,
  TIME_FORMAT,
} from './helpers/dates-manipulation';
import { getRecurrenceUntil, mapScheduleToRruleString } from './helpers/events-data-mappers';
import { CalendarEventObject } from './types';
import { useSignalRulesEngine } from './use-signal-rules-engine';

const INTERVAL_OPTION_LABELS = RECURRENCE_INTERVAL_OPTIONS.map((option) => option.label);

const WEEKDAY_OPTIONS = WEEKDAYS.map(({ label }) => ({
  id: label,
  value: label,
  label,
  testId: `signal-modal-weekday-${label}`,
}));

const mapOptions = (options: { label: string; value: string | number }[]) =>
  options.map((it) => ({
    ...it,
    id: it.value.toString(),
  }));

enum SignalManagementFields {
  PrimaryAgentId = 'primaryAgentId',
  SecondaryAgentId = 'secondaryAgentId',
  DateStart = 'dateStart',
  TimeStart = 'timeStart',
  TimeEnd = 'timeEnd',
  Recurrence = 'recurrence',
  IsAllDay = 'isAllDay',
  RecurrenceInterval = 'recurrenceInterval',
  RecurrencePeriod = 'recurrencePeriod',
  EditType = 'editType',
  Conditions = 'conditions',
  EnabledConditions = 'enabledConditions',
  RecurringDays = 'recurringDays',
}

export type SignalManagementFormValues = {
  [SignalManagementFields.PrimaryAgentId]: string;
  [SignalManagementFields.SecondaryAgentId]: string;
  [SignalManagementFields.DateStart]: dayjs.Dayjs;
  [SignalManagementFields.TimeStart]: string;
  [SignalManagementFields.TimeEnd]: string;
  [SignalManagementFields.Recurrence]: RecurrenceOptions;
  [SignalManagementFields.IsAllDay]: boolean;
  [SignalManagementFields.RecurrenceInterval]: string | undefined;
  [SignalManagementFields.RecurrencePeriod]: Frequency | undefined;
  [SignalManagementFields.EditType]: EditRecurrence | undefined;
  [SignalManagementFields.Conditions]: (RuleGroupType & { rules: RulesArray }) | undefined;
  [SignalManagementFields.EnabledConditions]: boolean;
  [SignalManagementFields.RecurringDays]: string[];
};

export interface SignalOnCallScheduleModalProps {
  partnerId: number;
  events: OncallManagementScheduleRecord[];
  agents: ServerPlatformUserRaw[];
  timezone?: string;
  data: CalendarEventObject | null;
  onSubmit: (event: OncallManagementScheduleMutationRecord, isEditing?: boolean) => void;
  onDelete: (eventId: string) => void;
  onClose: () => void;
}

const validateOnCallSchedule = (values: SignalManagementFormValues) => {
  const startDate = !values[SignalManagementFields.IsAllDay]
    ? dayjs(
        mergeDateAndTime(
          values[SignalManagementFields.DateStart].toDate(),
          values[SignalManagementFields.TimeStart],
        ),
      )
    : dayjs(values[SignalManagementFields.DateStart]);

  if (!values[SignalManagementFields.IsAllDay]) {
    const endDate = dayjs(
      mergeDateAndTime(
        values[SignalManagementFields.DateStart].toDate(),
        values[SignalManagementFields.TimeEnd],
      ),
    );
    if (endDate.isBefore(startDate) || endDate.isSame(startDate)) {
      return {
        [SignalManagementFields.TimeStart]: ' ',
        [SignalManagementFields.TimeEnd]: 'End time should be later than start time',
      };
    }
  }

  const todayStartDay = dayjs().startOf('day').toDate();

  if (startDate.isBefore(todayStartDay, 'day')) {
    return {
      [SignalManagementFields.DateStart]: 'Start date should be in the future',
    };
  }
  return {};
};

const formDataToOnCallManagementScheduleDataInput = ({
  values,
  timezone,
  partnerId,
}: {
  values: SignalManagementFormValues;
  timezone: string;
  partnerId: number;
}): Omit<OncallManagementScheduleDataInput, 'oncallManagementScheduleId'> => {
  let start;
  let end;
  let recurrence = values[SignalManagementFields.Recurrence];

  if (!values[SignalManagementFields.IsAllDay]) {
    start = mergeDateAndTime(
      values[SignalManagementFields.DateStart].toDate(),
      values[SignalManagementFields.TimeStart],
    );
    end = mergeDateAndTime(
      values[SignalManagementFields.DateStart].toDate(),
      values[SignalManagementFields.TimeEnd],
    );
  } else {
    start = dayjs(values[SignalManagementFields.DateStart]).startOf('day').toDate();
    end = dayjs(values[SignalManagementFields.DateStart]).endOf('day').toDate();
  }
  if (
    isWeeklyPeriodSelected(values) ||
    values[SignalManagementFields.Recurrence] === RecurrenceOptions.Custom
  ) {
    recurrence = mapScheduleToRruleString(values) as RecurrenceOptions;
  }

  const persistConditions = values[SignalManagementFields.EnabledConditions];
  const formConditions = values[SignalManagementFields.Conditions];
  const completeRules = persistConditions
    ? pruneIncompleteConditions(formConditions?.rules)
    : [];
  const conditions = completeRules.length
    ? {
        elementType: 'conditionGroup',
        operator: formConditions?.combinator,
        conditionsElements: completeRules.map(mapUIRulesForRequest),
      }
    : null;

  const event: Omit<OncallManagementScheduleDataInput, 'oncallManagementScheduleId'> = {
    partnerId,
    primaryAgentId: values[SignalManagementFields.PrimaryAgentId],
    secondaryAgentId: values[SignalManagementFields.SecondaryAgentId]
      ? values[SignalManagementFields.SecondaryAgentId]
      : null,
    startTime: addTimeZone(start, timezone, true).toISOString(),
    endTime: addTimeZone(end, timezone, true).toISOString(),
    recurrence,
    recurrenceUntil: getRecurrenceUntil(recurrence),
    conditions: JSON.stringify(conditions),
  };
  return event;
};

export const SignalOnCallScheduleModal = () => {
  const { modalProps } = useModalState(Modals.SignalOnCallScheduleModal);
  const { closeModal } = useModalMethods(Modals.SignalOnCallScheduleModal);

  const { confirm, renderConfirmationBody } = useConfirmationModal();

  const { agents = [], timezone = '', data, partnerId, events } = modalProps || {};

  const initialEvent = useMemo(
    () => (events || []).find((it) => it.oncallManagementScheduleId === data?.id),
    [events, data?.id],
  );

  const { createOnCallScheduleEvent, isLoading: isCreating } =
    useCreateSignalOnCallScheduleEvent();
  const { updateOnCallScheduleEvent, isLoading: isUpdating } =
    useUpdateSignalOnCallScheduleEvent({
      partnerId: partnerId!,
      initialEvent,
      partnerTimezone: timezone,
    });

  const { deleteOnCallScheduleEvent } = useDeleteSignalOnCallScheduleEvent({
    partnerId: partnerId!,
    partnerTimezone: timezone,
    initialEvent,
  });

  const { showErrorAlert, showSuccessAlert } = useNotifications();

  const ruleEngineConfig = useSignalRulesEngine();

  const isEditing = Boolean(data?.id);

  const hasCustomRecurrence = data?.recurrence
    ? !Object.values(RecurrenceOptions).includes(data.recurrence)
    : false;

  const defaultFormValues = useMemo<SignalManagementFormValues>(
    () => ({
      [SignalManagementFields.PrimaryAgentId]: data?.primaryAgentId ?? '',
      [SignalManagementFields.SecondaryAgentId]: data?.secondaryAgentId ?? '',
      [SignalManagementFields.DateStart]: dayjs(data?.start),
      [SignalManagementFields.TimeStart]: data?.start
        ? dayjs(data?.start).format(TIME_FORMAT)
        : '',
      [SignalManagementFields.TimeEnd]: data?.end ? dayjs(data?.end).format(TIME_FORMAT) : '',
      [SignalManagementFields.IsAllDay]: data ? isAllDaySchedule(data) : false,
      [SignalManagementFields.Recurrence]: hasCustomRecurrence
        ? RecurrenceOptions.Custom
        : (data?.recurrence ?? RecurrenceOptions.Once),
      [SignalManagementFields.RecurrenceInterval]:
        getRecurrenceInterval(data?.recurrence)?.toString() ?? undefined,
      [SignalManagementFields.RecurrencePeriod]:
        getRecurrencePeriod(data?.recurrence) ?? undefined,
      [SignalManagementFields.EditType]: EditRecurrence.This,
      // @ts-expect-error types mismatch and have no idea how to make it happy.
      [SignalManagementFields.Conditions]:
        initialEvent && ruleEngineConfig.conditions.length > 0
          ? {
              combinator: initialEvent.conditions?.operator || 'and',
              not: false,
              rules:
                initialEvent.conditions?.conditionsElements?.map((condition) =>
                  mapConditionToFormValues(condition, ruleEngineConfig),
                ) || [],
            }
          : undefined,
      [SignalManagementFields.EnabledConditions]:
        (initialEvent?.conditions?.conditionsElements?.length || 0) > 0,
      [SignalManagementFields.RecurringDays]: getRecurrenceDayLabels(data?.recurrence),
    }),
    [data, hasCustomRecurrence, initialEvent, ruleEngineConfig],
  );

  const formMethods = useForm<SignalManagementFormValues>({
    defaultValues: defaultFormValues,
    mode: 'all',
  });

  const {
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    control,
    reset,
  } = formMethods;

  const { field: conditionsField } = useController({
    control,
    name: SignalManagementFields.Conditions,
    rules: {
      validate: (value, formValues) =>
        !formValues[SignalManagementFields.EnabledConditions] ||
        getValidConditionsCount(value) > 0 ||
        'Add at least one complete condition to save this schedule',
    },
  });

  useEffect(() => {
    if (defaultFormValues && !isDirty) {
      reset(defaultFormValues, { keepDirty: true });
    }
  }, [defaultFormValues, reset, isDirty]);

  const watchableDateStart = watch(SignalManagementFields.DateStart);
  const watchablePrimaryAgentId = watch(SignalManagementFields.PrimaryAgentId);
  const watchableRecurrence = watch(SignalManagementFields.Recurrence);
  const watchableRecurrencePeriod = watch(SignalManagementFields.RecurrencePeriod);
  const watchableIsAllDay = watch(SignalManagementFields.IsAllDay);
  const watchableEditType = watch(SignalManagementFields.EditType);
  const watchableEnabledConditions = watch(SignalManagementFields.EnabledConditions);

  const [isLoading, setIsLoading] = useState(false);
  const [deleteChoice, setDeleteChoice] = useState<DeleteRecurrence>(DeleteRecurrence.This);
  const [isRecurringDeleteFlow, setIsRecurringDeleteFlow] = useState(false);
  const deleteChoiceRef = useRef<DeleteRecurrence>(DeleteRecurrence.This);

  const onClose = useCallback(() => {
    closeModal();
    modalProps?.onClose?.();
  }, [closeModal, modalProps]);

  const onSubmit = useCallback(
    async (values: SignalManagementFormValues) => {
      setIsLoading(true);

      const event = formDataToOnCallManagementScheduleDataInput({
        values,
        timezone,
        partnerId: partnerId!,
      });

      try {
        if (isEditing) {
          const result = await updateOnCallScheduleEvent(
            { ...event, oncallManagementScheduleId: data?.id! },
            canEditCurrentEvent(data) ? values[SignalManagementFields.EditType] : undefined,
          );

          if (result) {
            showSuccessAlert('Event was updated successfully');
            if (result.updatedEvent) {
              modalProps?.onSubmit?.(result.updatedEvent, true);
            }
            result.createdEvents.forEach((createdEvent) => {
              modalProps?.onSubmit?.(createdEvent, false);
            });
            if (result.deletedEventId) {
              modalProps?.onDelete?.(result.deletedEventId);
            }
          }
        } else {
          const response = await createOnCallScheduleEvent(event);

          if (response) {
            showSuccessAlert('Event was created successfully');
            modalProps?.onSubmit?.(response, false);
          }
        }
        onClose();
      } catch {
        // The mutation hooks already toast the failure; the dialog stays open with the input intact.
      } finally {
        setIsLoading(false);
      }
    },
    [
      createOnCallScheduleEvent,
      data,
      isEditing,
      modalProps,
      onClose,
      partnerId,
      showSuccessAlert,
      timezone,
      updateOnCallScheduleEvent,
    ],
  );

  const handleDeleteWithMode = useCallback(
    async (deleteMode: DeleteRecurrence) => {
      if (!data?.id) {
        return;
      }

      setIsLoading(true);
      try {
        const result = await deleteOnCallScheduleEvent(
          {
            ...formDataToOnCallManagementScheduleDataInput({
              values: getValues(),
              timezone,
              partnerId: partnerId!,
            }),
            oncallManagementScheduleId: data.id,
          },
          deleteMode,
        );

        if (result?.updatedEvent) {
          modalProps?.onSubmit?.(result.updatedEvent, true);
        }

        if (result?.createdEvent) {
          modalProps?.onSubmit?.(result.createdEvent, false);
        }

        if (result?.deletedEventId) {
          modalProps?.onDelete?.(result.deletedEventId);
        }

        onClose();
      } catch {
        showErrorAlert('Something went wrong deleting schedule event, please try again');
      } finally {
        setIsLoading(false);
      }
    },
    [
      data?.id,
      deleteOnCallScheduleEvent,
      getValues,
      modalProps,
      onClose,
      partnerId,
      showErrorAlert,
      timezone,
    ],
  );

  const handleDeleteSingleEvent = useCallback(async () => {
    const confirmed = await confirm({
      title: 'Delete event?',
      body: 'This on-call event will be deleted permanently.',
      confirmLabel: 'Delete event',
      danger: true,
    });

    if (!confirmed) {
      return;
    }

    await handleDeleteWithMode(DeleteRecurrence.ThisAndFollowing);
  }, [confirm, handleDeleteWithMode]);

  const setDeleteChoiceValue = useCallback((value: DeleteRecurrence) => {
    deleteChoiceRef.current = value;
    setDeleteChoice(value);
  }, []);

  const handleDeleteRecurringEvent = useCallback(async () => {
    setDeleteChoiceValue(DeleteRecurrence.This);
    setIsRecurringDeleteFlow(true);

    const confirmed = await confirm({
      title: 'Delete recurring event?',
      body: 'This event repeats, so choose which occurrences to delete.',
      confirmLabel: 'Delete event',
      danger: true,
    });

    setIsRecurringDeleteFlow(false);

    if (!confirmed) {
      return;
    }

    await handleDeleteWithMode(deleteChoiceRef.current);
  }, [confirm, handleDeleteWithMode, setDeleteChoiceValue]);

  const handleDeleteClick = useCallback(async () => {
    if (!data?.id) {
      return;
    }

    const isRecurringEvent = !!data.recurrence && data.recurrence !== RecurrenceOptions.Once;

    if (!isRecurringEvent) {
      await handleDeleteSingleEvent();
      return;
    }

    await handleDeleteRecurringEvent();
  }, [data?.id, data?.recurrence, handleDeleteRecurringEvent, handleDeleteSingleEvent]);

  const onAllDayChange = useCallback(() => {
    const isAllDay = getValues(SignalManagementFields.IsAllDay);
    setValue(
      SignalManagementFields.TimeStart,
      isAllDay ? '' : watchableDateStart.format(TIME_FORMAT),
    );
    setValue(
      SignalManagementFields.TimeEnd,
      isAllDay ? '' : watchableDateStart.add(30, 'minute').format(TIME_FORMAT),
    );
  }, [getValues, setValue, watchableDateStart]);

  const onRecurrenceChange = useCallback(
    (value: Option['value']) => {
      const selectedRecurrence = value as RecurrenceOptions;
      setValue(SignalManagementFields.Recurrence, selectedRecurrence);

      if (selectedRecurrence === RecurrenceOptions.Custom) {
        setValue(SignalManagementFields.RecurrenceInterval, '1');
        setValue(SignalManagementFields.RecurrencePeriod, Frequency.DAILY);
      } else {
        setValue(SignalManagementFields.RecurrenceInterval, undefined);
        setValue(SignalManagementFields.RecurrencePeriod, undefined);
      }

      if (!isWeeklyPeriodSelected({ recurrence: selectedRecurrence })) {
        setValue(SignalManagementFields.RecurringDays, []);
      } else {
        /*
          If user selects weekly recurrence, we should pre-select current day
          to build proper RRule that would allow easier schedule editing.
        */
        if (getValues(SignalManagementFields.RecurringDays).length === 0) {
          const currentDay = watchableDateStart.day();
          setValue(SignalManagementFields.RecurringDays, [WEEKDAYS[currentDay].label]);
        }
      }
    },
    [setValue, getValues, watchableDateStart],
  );

  const onEditRecurrence = useCallback(
    (value: Option['value']) => {
      setValue(SignalManagementFields.EditType, value as EditRecurrence);
    },
    [setValue],
  );

  const agentsOptions = useMemo(
    () =>
      agents.map((it) => ({
        id: it.id,
        label: getFullName(it),
        value: it.id,
      })),
    [agents],
  );

  const secondaryAgentsUsersPool = useMemo(() => {
    if (!watchablePrimaryAgentId) {
      return agentsOptions;
    }

    return agentsOptions.filter((it) => it.id !== watchablePrimaryAgentId);
  }, [agentsOptions, watchablePrimaryAgentId]);

  const isCustomRecurrenceSelected = watchableRecurrence === RecurrenceOptions.Custom;
  const isOneTimeEvent = watchableRecurrence === RecurrenceOptions.Once;

  const conditions = ruleEngineConfig.conditions.filter((c) =>
    c.triggers.some((t) =>
      [BehaviorTrigger.ConversationCreated, BehaviorTrigger.ConversationUpdated].includes(t),
    ),
  );

  const submitButtonIsLoading = isCreating || isUpdating || isLoading;

  return (
    <ModalContainer
      open
      size="large"
      title={isEditing ? 'Edit On-Call Schedule' : 'Add On-Call Schedule'}
      data-testid="signal-modal"
      onClose={onClose}
      headerActions={
        isEditing
          ? [
              {
                label: 'Delete event',
                icon: <Trash2 size={16} />,
                onClick: handleDeleteClick,
                testId: 'signal-modal-delete',
              },
            ]
          : undefined
      }
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'signal-modal-cancel' },
        {
          label: isEditing ? 'Save schedule' : 'Add schedule',
          kind: 'primary',
          onClick: handleSubmit(onSubmit),
          disabled: !isValid,
          loading: submitButtonIsLoading,
          testId: 'signal-modal-save',
        },
      ]}
    >
      <GenericForm<SignalManagementFormValues> formMethods={formMethods}>
        <FormSelect
          required
          type="select"
          data-testid="signal-modal-primary-agent"
          name={SignalManagementFields.PrimaryAgentId}
          label="Primary On-Call User"
          options={agentsOptions}
        />
        <FormSelect
          type="select"
          data-testid="signal-modal-secondary-agent"
          name={SignalManagementFields.SecondaryAgentId}
          label="Secondary On-Call User"
          options={secondaryAgentsUsersPool}
          clearable
        />
        <Stack gap={2}>
          <Stack direction="row" gap={1} alignItems="flex-end">
            <FormDatePicker
              required
              type="datepicker"
              name={SignalManagementFields.DateStart}
              label="Starting On"
              fullWidth
              rules={{
                validate: (_: string, formValues: SignalManagementFormValues) =>
                  validateOnCallSchedule(formValues)[SignalManagementFields.DateStart] || true,
              }}
            />
            <FormSwitch
              type="switch"
              data-testid="signal-modal-all-day"
              name={SignalManagementFields.IsAllDay}
              label="All day"
              sx={{
                height: V2_FIELD_HEIGHT.small,
                justifyContent: 'center',
                whiteSpace: 'nowrap',
              }}
              switchFieldProps={{ inputProps: { 'aria-label': 'All day' } }}
              onChange={onAllDayChange}
              rules={{
                deps: SignalManagementFields.DateStart,
              }}
            />
          </Stack>
          {!!errors[SignalManagementFields.DateStart]?.message && (
            <FormHelperText error>
              {errors[SignalManagementFields.DateStart]?.message}
            </FormHelperText>
          )}
          {!watchableIsAllDay && (
            <Stack direction="row" gap={1}>
              <FormSelect
                required={!watchableIsAllDay}
                type="select"
                data-testid="signal-modal-time-start"
                name={SignalManagementFields.TimeStart}
                label="From"
                options={mapOptions(TIME_SLOT_OPTIONS)}
                sx={{ flex: 1 }}
                rules={{
                  deps: [SignalManagementFields.DateStart, SignalManagementFields.TimeEnd],
                  validate: (_: string, formValues: SignalManagementFormValues) =>
                    validateOnCallSchedule(formValues)[SignalManagementFields.TimeStart] ||
                    true,
                }}
              />
              <FormSelect
                required={!watchableIsAllDay}
                type="select"
                data-testid="signal-modal-time-end"
                name={SignalManagementFields.TimeEnd}
                label="To"
                options={mapOptions(TIME_SLOT_OPTIONS)}
                sx={{ flex: 1 }}
                rules={{
                  deps: [SignalManagementFields.DateStart, SignalManagementFields.TimeStart],
                  validate: (_: string, formValues: SignalManagementFormValues) =>
                    validateOnCallSchedule(formValues)[SignalManagementFields.TimeEnd] || true,
                }}
              />
            </Stack>
          )}
          {!!errors[SignalManagementFields.TimeEnd]?.message && (
            <FormHelperText error data-testid="signal-modal-time-error">
              {errors[SignalManagementFields.TimeEnd]?.message}
            </FormHelperText>
          )}
        </Stack>
        {canEditCurrentEvent(data) && watchableRecurrence !== RecurrenceOptions.Once && (
          <FormSelect
            type="select"
            data-testid="signal-modal-edit-type"
            name={SignalManagementFields.EditType}
            label="Edit recurring schedule"
            options={mapOptions(UNTIL_SLOTS)}
            onChange={onEditRecurrence}
          />
        )}
        {(!isEditing ||
          (canEditCurrentEvent(data) && watchableEditType !== EditRecurrence.This)) && (
          <>
            <FormSelect
              type="select"
              data-testid="signal-modal-recurrence"
              name={SignalManagementFields.Recurrence}
              label="Recurrence"
              options={mapOptions(RECURRENCE_OPTIONS)}
              onChange={onRecurrenceChange}
            />
            {isCustomRecurrenceSelected ? (
              <Stack direction="row" gap={1.5}>
                <FormAutocomplete
                  type="autocomplete"
                  data-testid="signal-modal-interval"
                  name={SignalManagementFields.RecurrenceInterval}
                  label="Repeat every"
                  options={INTERVAL_OPTION_LABELS}
                  clearable={false}
                  sx={{ flex: '0 0 8rem', width: 'auto' }}
                />
                <FormSelect
                  type="select"
                  data-testid="signal-modal-period"
                  name={SignalManagementFields.RecurrencePeriod}
                  label="Period"
                  options={mapOptions(RECURRENCE_PERIOD_OPTIONS)}
                  sx={{ flex: 1 }}
                />
              </Stack>
            ) : null}
            {!isOneTimeEvent &&
              isWeeklyPeriodSelected({
                recurrence: watchableRecurrence,
                recurrencePeriod: watchableRecurrencePeriod,
              }) && (
                <FormToggleGroup
                  type="toggle-group"
                  multiple
                  name={SignalManagementFields.RecurringDays}
                  label="Repeat on"
                  options={WEEKDAY_OPTIONS}
                />
              )}
          </>
        )}
        <FormSwitch
          type="switch"
          data-testid="signal-modal-filter-by-conditions"
          name={SignalManagementFields.EnabledConditions}
          label="Filter by conditions"
          switchFieldProps={{ inputProps: { 'aria-label': 'Filter by conditions' } }}
          rules={{
            deps: SignalManagementFields.Conditions,
          }}
        />
        {errors[SignalManagementFields.Conditions]?.message ? (
          <FormHelperText error data-testid="signal-modal-conditions-error">
            {errors[SignalManagementFields.Conditions]?.message}
          </FormHelperText>
        ) : null}
        {watchableEnabledConditions ? (
          <QueryBuilderMaterial muiComponents={muiComponents}>
            <ConditionsBuilder
              data-testid="signal-modal-conditions"
              enableMountQueryChange={false}
              autoSelectField
              resetOnOperatorChange
              resetOnFieldChange
              context={{
                timezone: ruleEngineConfig.timezone,
                queryOptions: ruleEngineConfig.queryOptions,
              }}
              query={conditionsField.value}
              onQueryChange={conditionsField.onChange}
              fields={conditions as unknown as FullField[]}
              // @ts-expect-error Overriding default library typings because of custom fields structure.
              getOperators={(field: SelectorValue) => {
                return field?.operators || [];
              }}
              getDefaultValue={getConditionDefaultValue}
              // @ts-expect-error The editor reads the rules engine's field shape, wider than FullField.
              valueEditor={ConditionValueEditor}
            />
          </QueryBuilderMaterial>
        ) : null}
      </GenericForm>
      {isRecurringDeleteFlow
        ? renderConfirmationBody(
            <RadioGroup
              name="recurring-delete-choice"
              value={deleteChoice}
              sx={{ px: 2 }}
              aria-label="Recurring delete scope"
              onChange={(event) =>
                setDeleteChoiceValue(event.target.value as DeleteRecurrence)
              }
            >
              <FormControlLabel
                value={DeleteRecurrence.This}
                control={<Radio />}
                label="Only this event"
              />
              <FormControlLabel
                value={DeleteRecurrence.ThisAndFollowing}
                control={<Radio />}
                label="This and all following events"
              />
            </RadioGroup>,
          )
        : null}
    </ModalContainer>
  );
};
